const Address = require('@/lib/Addresses/Address');
const Contact = require('@/lib/Contacts/Contact');
const CustomerAccountType = require('@/lib/Customers/CustomerAccountType');
const Membership = require('@/lib/Memberships/Membership');
const { hydrate, hydrateArray } = require('@/lib/Hydration');
const { populate } = require('@/functions/objects');
const { toInt, toNullableString, toString } = require('@/functions/transform');

/**
 * @param {object} [data]
 *
 * @constructor
 */
const CustomerAccount = function (data = {}) {
    /**
     * @type {number|null}
     */
    this.ID = null;

    /**
     * @type {string|null}
     */
    this.email = null;
    this.organisation = null;
    this.type = CustomerAccountType.PERSONAL;
    this.ref = null;

    /**
     * @type {Address[]}
     */
    this.addresses = [];

    /**
     * @type {Address}
     */
    this.primaryAddress = null;

    /**
     * @type {Contact[]}
     */
    this.contacts = [];

    /**
     * @type {Contact}
     */
    this.primaryContact = null;

    /**
     * @type {Membership[]}
     */
    this.memberships = [];

    /**
     * @type {Membership|null}
     */
    this.firstActiveMembership = null;

    populate(this, data);
};

CustomerAccount.prototype = {
    /**
     * @returns {boolean}
     */
    isBusiness() {
        return this.type === CustomerAccountType.BUSINESS;
    },

    getHydrationMap() {
        return {
            addresses: {
                field: ['savedAddresses', 'addresses'],
                modelCollection: Address,
            },
            contacts: {
                modelCollection: Contact,
            },
            email: toNullableString,
            ID: {
                field: ['ID', 'customerAccountID'],
                transform: toInt,
            },
            memberships: {
                modelCollection: Membership,
            },
            organisation: toNullableString,
            primaryAddress: {
                model: Address,
            },
            primaryContact: {
                model: Contact,
            },
            ref: toNullableString,
            type: toString,
        };
    },

    afterHydration(data) {
        // Hydrate the 'contacts' property here as it is more complex than getHydrationMap should handle.
        if (data.hasOwnProperty('accountContacts')) {
            /**
             * For accounts coming from the API the accountContacts property is in the format:
             * {
             *     0: {
             *         accountContactID: 123,
             *         contact: {..},
             *         type: 'Primary'
             *     }
             * }
             *
             * Convert it to a regular array of just contact models.
             */
            let contactsData = data.accountContacts;

            let contacts = [];
            for (let i = 0; i < contactsData.length; i++) {
                let contactData = contactsData[i].hasOwnProperty('contact') ? contactsData[i].contact : contactsData[i];
                let contact = hydrate(
                    contactData,
                    new Contact()
                );

                // If the type is Primary, also set the primaryContact
                if (contactsData[i].type === 'Primary') {
                    this.primaryContact = contact;
                }

                contacts.push(contact);
            }

            this.contacts = contacts;
        } else if (data.hasOwnProperty('contacts')) {
            /**
             * If this is from a model that has already been converted to the EPOS format and stashed
             * somewhere, the 'contact' property will just be a list of contact data so we don't
             * need to do the above.
             */
            this.contacts = hydrateArray(
                data.contacts,
                function () {
                    return new Contact();
                }
            );
        }

        // Determine primary contact.
        if (!this.primaryContact && this.contacts.length > 0) {
            this.primaryContact = this.contacts[0];
        }

        this.refreshPrimaryAddress();

        // Sort contacts by name.
        this.contacts.sort(function (a, b) {
            let aFullName = a.getFullName();
            let bFullName = b.getFullName();

            if (aFullName === bFullName) {
                return 0;
            }

            return aFullName < bFullName ? -1 : 1;
        });

        // Remove any "dead" memberships. The API must return memberships that are archived/deleted, but this
        // EPOS must hide them completely.
        this.memberships = this.memberships.filter(function removeDeadMemberships(membership) {
            return !membership.isDead();
        });

        this.firstActiveMembership = this.getActiveMembership();
    },

    /**
     * Sort the addresses of this account to make the primary address the first one in the list
     * if there is one. Then set the primary address as the first result from the list.
     * If there is no primary address it will just use the first (oldest) address as the primary.
     */
    refreshPrimaryAddress() {
        if (this.addresses.length > 0) {
            this.sortAddresses();
            this.primaryAddress = this.addresses[0];
        } else {
            this.primaryAddress = null;
        }
    },

    /**
     * Sort the addresses on the account.
     * Sorts oldest to newest (by address ID) and then brings the primary address to the top.
     */
    sortAddresses() {
        this.addresses = this.addresses
            .sort((a, b) => (a.ID - b.ID))
            .sort((a, b) => (b.defaultBilling - a.defaultBilling));
    },

    /**
     * Returns the first active Membership.
     *
     * @returns {Membership|null}
     */
    getActiveMembership: function getActiveMembership() {
        for (let i = 0; i < this.memberships.length; i++) {
            let membership = this.memberships[i];
            if (membership.isActive()) {
                return membership;
            }
        }

        return null;
    },

    /**
     * @returns {string}
     */
    getAccountDetailsPath() {
        return '/accounts/' + this.ID;
    },
};

module.exports = CustomerAccount;
